// The seeded templates (M1-06; 01 Template: "Client onboarding"). Every company is created with
// them (operator company creation, `services/api/src/domain/templates/catalog.ts`); the web's
// fake twin seeds the same data. Metric keys name the DE metric catalog every operator-created
// company has (13 §DE scorecard catalog); a company without them gets the responsibility and a
// `skip` line for the metric, never an invented definition.
import type { TemplateSpec } from "@companyos/schema/contracts";

export type CatalogTemplate = { name: string; description: string; spec: TemplateSpec };

export const CLIENT_ONBOARDING: CatalogTemplate = {
  name: "Client onboarding",
  description:
    "An account manager and an onboarding specialist for a new client, their team, weekly and " +
    "monthly check-ins, and the client's space with an onboarding board.",
  spec: {
    version: 1,
    params: [{ key: "client_name", label: "Client name", required: true }],
    roles: [
      {
        key: "account_manager",
        name: "Account manager",
        description: "Owns the client relationship and its results.",
      },
      {
        key: "onboarding_specialist",
        name: "Onboarding specialist",
        description: "Runs the first weeks of a new client: access, set-up, first report.",
      },
    ],
    teams: [{ key: "client_team", name: "{{client_name}} team", lead_seat: "account_manager" }],
    seats: [
      {
        key: "account_manager",
        role: "account_manager",
        title: "{{client_name}} account manager",
        team: "client_team",
      },
      {
        key: "onboarding_specialist",
        role: "onboarding_specialist",
        title: "{{client_name}} onboarding specialist",
        supervisor: "account_manager",
        team: "client_team",
      },
    ],
    responsibilities: [
      {
        seat: "account_manager",
        name: "{{client_name}} relationship",
        description: "Weekly check-in and the monthly account review.",
        metric_keys: ["de.outcomes"],
        recurring: [
          {
            schedule: "FREQ=WEEKLY;BYDAY=MO;BYHOUR=9",
            task: {
              title: "Weekly check-in with {{client_name}}",
              due_in_days: 1,
              checklist: ["Review last week's numbers", "Agree this week's priorities"],
            },
          },
          {
            schedule: "FREQ=MONTHLY;BYDAY=1MO;BYHOUR=10",
            task: { title: "Monthly account review: {{client_name}}", due_in_days: 3 },
          },
        ],
      },
      {
        seat: "onboarding_specialist",
        name: "{{client_name}} onboarding",
        description: "Access, set-up and the first report, tracked on the onboarding board.",
        metric_keys: ["de.tasks_completed"],
        recurring: [
          {
            schedule: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR;BYHOUR=9;BYMINUTE=30",
            board: "onboarding",
            task: { title: "Onboarding stand-up: {{client_name}}" },
          },
        ],
      },
    ],
    spaces: [
      {
        key: "client_space",
        name: "{{client_name}}",
        kind: "client",
        team: "client_team",
        channels: [{ name: "onboarding" }, { name: "internal", is_private: true }],
        boards: [
          {
            key: "onboarding",
            name: "{{client_name}} onboarding",
            statuses: [
              { key: "todo", name: "To do", done: false },
              { key: "doing", name: "In progress", done: false },
              { key: "done", name: "Done", done: true },
            ],
            tasks: [
              { title: "Kick-off call with {{client_name}}", priority: 2, due_in_days: 3 },
              { title: "Collect system access", due_in_days: 5 },
              { title: "First weekly report", due_in_days: 10 },
            ],
          },
        ],
      },
    ],
  },
};

/** The templates every new company starts with. */
export const SEEDED_TEMPLATES: readonly CatalogTemplate[] = [CLIENT_ONBOARDING];
